<script setup>
import { computed, defineOptions, reactive, ref, watch } from 'vue';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { FreeMode, Navigation, Thumbs } from 'swiper/modules';
import swal from 'sweetalert';
import CombinationService from '@/services/combination-service';

import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/navigation';
import 'swiper/css/thumbs';

defineOptions({
  name: 'ProductItem'
});

const emits = defineEmits(['combinationUpdate']);

const props = defineProps(
  {
    type: {
      default: 'normal',
      type: String,
    },
    subtype: {
      default: 'normal',
      type: String,
    },
    data: Object,
  }
);

const data = props.data;

const state = reactive({
  quantity: 1,
  currentVariant: data.currentVariant || null,
  hasSubVariants: data.product.variants !== 0,
  selected: {},
  favorited: data.favorited,
  selectedUidList: [],
  cableLength: data.currentVariant?.length || null,
});

if (!state.hasSubVariants && !state.currentVariant) {
  state.currentVariant = data.mainVariant;
}

// Favorite
const favoriteBtn = ref(null);

watch(() => favoriteBtn.value, () => {
  favoriteBtn.value.addEventListener('favorited', function (v) {
    state.favorited = v.detail.favorited ? 1 : 0;
  });
});

if (props.type === 'combination' && data.currentVariant) {
  state.selectedUidList = data.currentVariant.options.map(v => v.uid);

  data.features.forEach(function (feature) {
    Object.values(feature.options).forEach(function (option) {
      if (state.selectedUidList.includes(option.uid)) {
        toggleOption(option, feature);
      }
    });
  });
}

// Images
const images = computed(() => {
  let images = [];

  if (state.currentVariant && state.currentVariant.cover) {
    images.push(state.currentVariant.cover);
  } else {
    images.push(data.mainVariant.cover);
  }

  data.mainVariant.images.forEach(v => images.push(v.url));

  return images;
});

const thumbsSwiper = ref(null);
const modules = ref([FreeMode, Navigation, Thumbs]);

function setThumbsSwiper(swiper) {
  thumbsSwiper.value = swiper;
}

// Quantity
const disabledAction = computed(() => {
  return !state.currentVariant || !state.currentVariant?.state;
});

watch(() => state.quantity, (qty) => {
  if (qty < 1) {
    state.quantity = 1;
  }

  if (qty > 99) {
    state.quantity = 99;
  }
});

// Features
const allSelected = computed(() => {
  return Object.values(data.features).length === Object.values(state.selected).length;
});

watch(() => state.selected, () => {
  if (allSelected.value) {
    findVariant();
  }
}, { deep: true });

async function findVariant() {
  const options = Object.values(state.selected).map(option => option.uid);

  const res = await u.$http.get(
    '@product_ajax/getVariant',
    {
      params: {
        product_id: data.product.id,
        options
      }
    }
  );

  state.currentVariant = res.data.data;
}

function toggleOption(option, feature) {
  state.selected[feature.id] = option;
}

async function addToCart() {
  if (!state.currentVariant?.state) {
    return;
  }

  try {
    const res = await u.$http.post(
      '@cart_ajax/addNormalItemToCart',
      {
        product_id: data.product.id,
        variant_id: state.currentVariant.id,
        quantity: state.quantity,
      }
    );

    updateCartButton(res.data.data);
    await goToCart();

    return res.data;
  } catch (e) {
    u.alert(e.message, '', 'warning');
  }
}

async function goToCart() {
  const v = await swal({
    title: '已加入購物車',
    buttons: {
      'return': {
        text: '繼續購物',
        value: false,
        className: 'btn btn-outline-secondary c-swal-cancel',
      },
      confirm: {
        text: '前往結帳',
        value: true,
        className: 'btn btn-primary c-swal-confirm',
      },
    }
  });

  if (v) {
    location.href = u.route('cart');
  }
}

function updateCartButton(data) {
  const count = data.length;

  const $cartButtons = document.querySelectorAll('[data-role=cart-button]');

  for (const $cartButton of $cartButtons) {
    const $cartQuantity = $cartButton.querySelector('[data-role=cart-quantity]');

    $cartButton.classList.toggle('h-has-items', count > 0);
    $cartQuantity.textContent = count;
  }
}

const cableLengthInput = ref(null);

function handleVariant() {
  if (props.subtype === 'cable') {

    if (!cableLengthInput.value.checkValidity()) {
      cableLengthInput.value.reportValidity();

      return;
    }
  }

  emits(
    'combinationUpdate',
    {
      item: state.currentVariant,
      length: state.cableLength
    }
  );
}

function setCableVariant(variant) {
  variant.options.forEach((v) => {
    state.selected[v.parentId] = v;
  })
}
</script>

<template>
  <section class="l-product-purchase">
    <div class="container-fluid p-0">
      <div class="row">
        <div class="col-12 col-md-5 mb-5 mb-md-0">
          <swiper
            :rewind="true"
            :spaceBetween="0"
            :thumbs="{ swiper: thumbsSwiper }"
            :modules="modules"
            :navigation="{ enabled: true, prevEl: '.swiper-button-prev', nextEl: '.swiper-button-next' }"
            class="c-product-swiper-large mb-3"
          >
            <swiper-slide v-for="(image, i) in images" :key="`thumb-swiper-img-` + i"
              class="ratio ratio-1x1"
            >
              <img :src="image" class="object-fit-cover" alt="Image" />
            </swiper-slide>
          </swiper>

          <div class="l-swiper-wrapper position-relative">
            <swiper
              @swiper="setThumbsSwiper"
              :rewind="true"
              :spaceBetween="16"
              :slidesPerView="4"
              :freeMode="true"
              :watchSlidesProgress="true"
              :modules="modules"
              class="c-product-swiper"
            >
              <swiper-slide v-for="(image, i) in images" :key="`product-swiper-img-` + i"
                class="ratio ratio-1x1"
              >
                <img :src="image" class="object-fit-cover" alt="Image" />
              </swiper-slide>
            </swiper>
            <div class="swiper-button-next">
              <i class="fa-regular fa-chevron-right"></i>
            </div>
            <div class="swiper-button-prev">
              <i class="fa-regular fa-chevron-left"></i>
            </div>
          </div>
        </div>

        <div class="col-12 col-md-7 ps-lg-5">
          <div class="d-flex flex-wrap gap-2">
            <div v-for="tag in data.tags" class="badge bg-primary">
              {{ tag.title }}
            </div>
          </div>

          <h1 class="h3 fw-medium mb-1">{{ data.product.title }}</h1>

          <div class="d-flex align-items-center gap-2">
            <p>{{ data.product.model }}</p>

            <template v-if="state.hasSubVariants && state.currentVariant && state.currentVariant.sku">
              <p>-</p>
              <p>{{ state.currentVariant.sku }}</p>
            </template>
          </div>

          <div class="l-product-purchase__price h2 fw-medium">
            <template v-if="state.hasSubVariants && !state.currentVariant && data.minPrice === data.maxPrice">
              {{ $formatPrice(data.maxPrice, true, true, $getDecimalPlace(data.maxPrice)) }}
            </template>

            <template v-else>
              <template v-if="state.hasSubVariants && !state.currentVariant">
                {{ $formatPrice(data.minPrice, true, true, $getDecimalPlace(data.minPrice)) }} - {{ $formatPrice(data.maxPrice, true, true, $getDecimalPlace(data.maxPrice)) }}
              </template>

              <template v-else>
                {{ $formatPrice(state.currentVariant.priceSet.final.price, true, true, $getDecimalPlace(state.currentVariant.priceSet.final.price)) }}
              </template>
            </template>
          </div>

          <div v-if="subtype === `cable` && data.features.length" class="l-product-purchase__specification">
            <div class="dropdown">
              <button
                class="btn btn c-choose-btn text-dark dropdown-toggle d-flex align-items-center justify-content-between w-100"
                type="button" id="variant-dropdown" data-bs-toggle="dropdown" aria-expanded="false">
                {{
                  state.currentVariant?.title
                    ? CombinationService.getVariantTitleText(state.currentVariant?.title)
                    : '請選擇'
                }}
              </button>
              <ul class="dropdown-menu l-product-purchase__dropdown-menu w-100" aria-labelledby="variant-dropdown">
                <li>
                  <a class="dropdown-item" href="javascript://" @click="state.currentVariant = null">
                    請選擇
                  </a>
                </li>

                <li v-for="variant in data.variants">
                  <a class="dropdown-item" href="javascript://" @click="setCableVariant(variant)">
                    {{ CombinationService.getVariantTitleText(variant.title) }}
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div v-else v-for="feature in data.features"
            class="l-product-purchase__specification">
            <h2 class="fs-6 fw-medium">
              {{ feature.title }}
            </h2>
            <div class="l-product-purchase__specification-wrapper d-flex flex-wrap gap-2">
              <div v-for="option in feature.options">
                <input type="radio" class="btn-check" :name="`feature-` + feature.id" :id="`option-` + option.uid"
                  @change="toggleOption(option, feature)"
                  :checked="state.selectedUidList.includes(option.uid)"
                  autocomplete="off">
                <label class="c-product-specification-btn btn" :for="`option-` + option.uid">
                  {{ option.text }}
                </label>
              </div>
            </div>
          </div>

          <template v-if="type === `combination`">
            <div v-if="data.category.combinationShowIn === `cable`"
              class="l-product-purchase__specification">
              <h2 class="fs-6 fw-medium">長度</h2>
              <div class="l-product-purchase__specification-wrapper d-flex flex-wrap gap-2">
                <input id="cable-length-input" ref="cableLengthInput" v-model="state.cableLength"
                  class="j-product-input__number form-control text-center" type="number"
                  min="8"
                  required
                >
                <label for="cable-length-input" class="col-form-label">
                  <span class="me-2">公分</span>
                  <span class="small text-muted">*最短長度為8公分</span>
                </label>
              </div>
            </div>

            <div class="row">
              <div class="col-12 l-product-purchase__specification">
                <button type="button" class="btn btn-primary w-100" data-role="add-to-combination"
                  :disabled="disabledAction"
                  @click="handleVariant"
                >
                  <i class="fa-regular fa-cart-shopping me-2"></i> 選擇
                </button>
              </div>
            </div>
          </template>

          <template v-if="type === `normal`">
            <div class="l-product-purchase__specification">
              <h2 class="fs-6 fw-medium">數量</h2>
              <div class="input-group input-number-group">
                <button type="button" class="c-product-specification-btn btn"
                  @click="state.quantity--"
                  :disabled="disabledAction"
                >
                  <i class="fa-regular fa-minus"></i>
                </button>
                <input v-model.number="state.quantity" data-role="quantity"
                  class="form-control text-center" type="number"
                  :disabled="disabledAction"
                >
                <button type="button" class="c-product-specification-btn btn"
                  @click="state.quantity++"
                  :disabled="disabledAction"
                >
                  <i class="fa-regular fa-plus"></i>
                </button>
              </div>
            </div>

            <div class="row">
              <div class="col-12 mb-3">
                <button type="button" class="btn btn-primary w-100"
                  :disabled="disabledAction"
                  @click="addToCart"
                >
                  <template v-if="!state.currentVariant || (state.currentVariant && state.currentVariant?.state)">
                    <i class="fa-regular fa-cart-shopping me-2"></i>加入購物車
                  </template>

                  <template v-else>
                    如需購買此規格組合，請洽本公司服務專員
                  </template>
                </button>
              </div>

              <div class="col-12 col-sm-6 mb-3 mb-sm-0">
                <!--沒檔案就disabled-->
                <a :href="data.product.files[0]?.url || 'javascript://'" class="btn btn-dark w-100"
                  :class="!data.product.files[0]?.url ? 'disabled' : ''"
                  :download="data.product.files[0]?.title"
                  :target="data.product.files[0]?.url ? '_blank' : '_self'"
                >
                  <i class="fa-regular fa-file-arrow-down me-2"></i>下載產品手冊
                </a>
              </div>
              <div class="col-12 col-sm-6">
                <button ref="favoriteBtn" class="c-favorite btn btn-outline-light w-100"
                  uni-favorite-button
                  :data-added="state.favorited"
                  data-type="product"
                  :data-id="data.product.id"
                  data-icon-active="fa-solid fa-star"
                  data-icon-inactive="fa-solid fa-star"
                  data-class-active="active"
                  data-class-inactive="inactive"
                  data-title-active=""
                  data-title-inactive=""
                >
                  <i class="fa-solid fa-star"></i>
                  <span class="ms-2">
                    {{ state.favorited ? '取消收藏' : '收藏商品' }}
                  </span>
                </button>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>

</style>
